import { useCallback, useMemo } from "react";
import DataTable from "./DataTable.jsx";
import { useDataTableColumns } from "../lib/columnSelection.js";
import { scopeStepLabel, useLabels } from "../lib/dimensionDefs.js";
import {
  TRACE_COLUMNS_3,
  TRACE_COLUMNS_4,
  unwrapTraceEntry,
  renderTraceCell,
  traceRowClassName,
  traceRowStyle,
} from "./TraceRow.jsx";

// Walk up to the page scroll container ([data-content-scroll]) and return its
// OverlayScrollbars viewport — trace virtualization measures scroll against it
// so the sticky header UX is preserved. Lens bodies render inside a ScrollArea
// with data-content-scroll, so this resolves there too.
export function findTraceScrollRoot(element) {
  if (!element) return null;
  const host =
    element.closest("[data-content-scroll]") ||
    element.closest("[data-endpoint-content-scroll]");
  if (!host) return null;
  return (
    host.querySelector("[data-overlayscrollbars-viewport]") ||
    host.querySelector(".os-viewport") ||
    host
  );
}

// Resolve an endpoint's friendly label from the lazy label cache,
// swapping it in for the row's id-prefix fallback. Mirrors
// TraceLogPanel.withEndpointLabel — kept local so the shared lens
// listing resolves names without depending on the panel module.
function withEndpointLabel(trace) {
  const id = trace?.endpointId;
  if (!id) return trace;
  const label = scopeStepLabel({ dim: "endpoints", id });
  if (!label || label === id || label === trace.endpointName) return trace;
  return { ...trace, endpointName: label };
}

// TraceTable — the presentational trace-log table: a standalone column header +
// virtualized rows via DataTable, each rendered through renderTraceCell (incl.
// per-row signal chips from `signalsByTraceId`). Shared by the trace log and the
// signals console so they look identical. Rows are "entries" (plain traces when
// contextMode is false); signal verdicts are passed IN, not computed here.
export default function TraceTable({
  rows = [],
  signalsByTraceId,
  selectedTraceId = "",
  onSelectTrace,
  showEndpoint = false,
  contextMode = false,
  loading = false,
  footer = null,
  // Optional toolbar (e.g. the scan-window scrubber + filter chips)
  // pinned WITH the column header in a sticky block — the trace
  // listing's pattern (TraceLogPanel): toolbar + column header stick
  // to the page scroll top, rows pass underneath. Renders even when
  // the row set is empty so window controls stay usable.
  toolbar = null,
  emptyMessage = "No requests in scope.",
}) {
  const TRACE_COLUMNS = showEndpoint ? TRACE_COLUMNS_4 : TRACE_COLUMNS_3;
  const minWidth = showEndpoint ? 480 : 420;
  const cols = useDataTableColumns({
    columns: TRACE_COLUMNS,
    defaultVisible: TRACE_COLUMNS.map((c) => c.id),
  });

  // Resolve endpoint names from the lazy label cache. Lens prompt
  // listings feed rows whose endpointName is just the id prefix (the
  // analytics adapters can't see the endpoint registry); useLabels
  // subscribes + triggers the fetch, and the returned tick re-renders
  // the cells as names land. Only relevant when the endpoint column shows.
  const endpointLabelSteps = useMemo(() => {
    if (!showEndpoint) return [];
    const seen = new Set();
    const steps = [];
    for (const entry of rows) {
      const { trace } = unwrapTraceEntry(entry, contextMode);
      const id = trace?.endpointId;
      if (!id || seen.has(id)) continue;
      seen.add(id);
      steps.push({ dim: "endpoints", id });
    }
    return steps;
  }, [showEndpoint, rows, contextMode]);
  const endpointLabelTick = useLabels(endpointLabelSteps);

  const renderCell = useCallback((entry, col) => {
    const u = unwrapTraceEntry(entry, contextMode);
    const trace = showEndpoint ? withEndpointLabel(u.trace) : u.trace;
    const signals = signalsByTraceId?.get(u.trace?.id);
    return renderTraceCell(trace, col, { ...u, signals });
  }, [contextMode, signalsByTraceId, showEndpoint, endpointLabelTick]);

  const rowClassName = useCallback((entry) => {
    const u = unwrapTraceEntry(entry, contextMode);
    return traceRowClassName({ active: u.trace?.id === selectedTraceId, isCtx: u.isCtx });
  }, [contextMode, selectedTraceId]);

  const rowStyle = useCallback((entry) => {
    const u = unwrapTraceEntry(entry, contextMode);
    return traceRowStyle({ active: u.trace?.id === selectedTraceId, isCtx: u.isCtx, score: u.score });
  }, [contextMode, selectedTraceId]);

  const onRowClick = useCallback((entry) => {
    const { trace } = unwrapTraceEntry(entry, contextMode);
    if (trace?.id) onSelectTrace?.(trace.id);
  }, [contextMode, onSelectTrace]);

  const getRowId = useCallback(
    (entry) => unwrapTraceEntry(entry, contextMode).trace?.id ?? "",
    [contextMode],
  );

  // Standalone column header — DataTable's own header is suppressed
  // (showHeader=false) so this one can live with the toolbar.
  const columnHeader = (
    <div className="overflow-hidden">
      <div className="will-change-transform" style={{ minWidth: `${minWidth}px` }}>
        <div
          className="grid items-center gap-x-2 border-y border-[color:var(--line-soft)] px-[6px] py-[4px] text-[9px] leading-[1.08] tracking-[0.08em] text-[color:var(--muted)] uppercase"
          style={{ gridTemplateColumns: cols.gridCols }}
        >
          {cols.effective.map((c) => (<span key={c.id}>{c.label}</span>))}
        </div>
      </div>
    </div>
  );

  return (
    <div>
      {toolbar ? (
        <div
          className="sticky z-20 grid gap-[8px] bg-[color:var(--bg)] pt-[8px] pb-[8px] shadow-[0_1px_0_var(--line-soft)] max-[900px]:static max-[900px]:shadow-none"
          style={{ top: 0 }}
        >
          {toolbar}
          {columnHeader}
        </div>
      ) : (
        columnHeader
      )}

      {rows.length ? (
        <div data-trace-log-scroll className="min-w-0">
          <DataTable
            cols={cols}
            rows={rows}
            getRowId={getRowId}
            renderCell={renderCell}
            loading={loading}
            showHeader={false}
            findScrollContainer={findTraceScrollRoot}
            minWidth={`${minWidth}px`}
            onRowClick={onRowClick}
            rowClassName={rowClassName}
            rowStyle={rowStyle}
            footer={footer}
          />
        </div>
      ) : loading ? (
        <div className="grid gap-[6px] py-[12px]">
          {Array.from({ length: 6 }, (_, i) => (
            <div key={i} className="h-[32px] rounded-[2px] bg-[color:var(--line-soft)]" style={{ opacity: 0.7 - i * 0.08 }} />
          ))}
        </div>
      ) : (
        <div className="grid gap-[4px] rounded-[2px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[10px] py-[12px]">
          <strong className="text-[13px] leading-[1.08] font-medium">{emptyMessage}</strong>
        </div>
      )}
    </div>
  );
}
